/**
 * Token source for the Reeve app — Clerk is the ONLY auth source.
 *
 * Real auth, always. `getAuthToken()` returns the signed-in Clerk session token,
 * which the backend's Clerk token handler validates and maps to the Reeve standard
 * claims (organization_id = the Clerk org = the tenant). Clerk attaches a global
 * `window.Clerk` once its provider mounts; the whole /app subtree is client-only,
 * so reading that global is safe. The token is null until the user is signed in
 * (and, in practice, has an active org) — the /app auth gate (AppAuthGate) prevents
 * any tenant-scoped API call from firing before sign-in, so a null token here is
 * effectively unreachable from the product surface; a stray call simply 401s.
 *
 * THE INVARIANT: the frontend never sees or sends a tenant id. The bearer token IS
 * the identity; the server resolves the tenant from it.
 */

/**
 * Minimal shape of the global Clerk singleton we read for the session token.
 * Clerk attaches `window.Clerk` when <ClerkProvider> mounts (client-only).
 * `loaded` flips true once Clerk has finished hydrating the session.
 */
declare global {
  interface Window {
    Clerk?: {
      loaded?: boolean;
      session?: { getToken: () => Promise<string | null> } | null;
    };
  }
}

/**
 * Resolves once `window.Clerk` exists and has finished initialising, so we never
 * read a not-yet-hydrated `session` (which would hand back a null token and fire an
 * authenticated request with no Authorization header — a spurious 401 that only
 * self-heals on a later re-render). Bounded poll so a genuinely-signed-out or
 * misconfigured state can't hang a request; after the timeout we read whatever is
 * there (typically null → the caller 401s, which is correct when truly signed out).
 */
async function waitForClerkReady(timeoutMs = 5000): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  while ((!window.Clerk || !window.Clerk.loaded) && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 40));
  }
}

/**
 * The bearer token for the current request: the live Clerk session token, or null
 * when no session/organization is active (callers let such a request 401).
 *
 * Async because Clerk's session.getToken() is async AND because we wait for Clerk to
 * finish hydrating first. Every token-attaching call site awaits this, so token
 * acquisition — and the readiness wait — is centralised here.
 */
export async function getAuthToken(): Promise<string | null> {
  await waitForClerkReady();
  const token = await window.Clerk?.session?.getToken();
  return token ?? null;
}
